import { Box, Typography } from '@mui/material';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../lib/components/atomic/button';
import { StatusBadge } from '../../../../lib/components/atomic/dataDisplay/statusBadge';
import { useStore } from '../../../../hooks/useStore';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback';
import { enqueueSnackbar } from 'notistack';
import { InformationDialog } from '../../../../lib/components';

interface Props {
  data: SingleSignOnData;
}

export interface SingleSignOnData {
  single_sign_on_enabled: boolean;
  password_auth_enabled: boolean;
  platform: boolean;
}

export const SingleSignOnSettingsView: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const { companyStore, authenticationStore } = useStore();
  const [openSsoActivationDialog, setOpenSsoActivationDialog] = useState(false);
  const [openSsoDeactivationDialog, setOpenSsoDeactivationDialog] = useState(false);
  const [openPasswordActivationDialog, setOpenPasswordActivationDialog] = useState(false);
  const [openPasswordDeactivationDialog, setOpenPasswordDeactivationDialog] = useState(false);

  const platform = data.platform ? 'PLATFORM' : 'APP';

  const handleConfirmation = async (sso: boolean, activated: boolean) => {
    const snackbarLoginMethodKey = sso ? 'SSO' : 'PASSWORD';
    const snackbarStateKey = activated ? 'ACTIVATED' : 'DEACTIVATED';

    try {
      if (sso) {
        data.platform
          ? await companyStore.updateSettings({
              platform_single_sign_on_enabled: activated
            })
          : await companyStore.updateSettings({
              app_single_sign_on_enabled: activated
            });
      } else if (data.platform) {
        await Promise.all(
          [
            companyStore.updateSettings({
              platform_password_auth_enabled: activated
            }),
            !activated &&
              (async () => {
                try {
                  await authenticationStore.disableTwoFactor();
                } catch (error) {
                  console.info('2FA was not enabled before: ', error);
                }
              })()
          ].filter(Boolean)
        );
      } else {
        await companyStore.updateSettings({
          app_password_auth_enabled: activated
        });
      }
    } catch (error) {
      console.error('Could not update login method: ', error);
      enqueueSnackbar({
        message: t('common:ERROR_OCCURED'),
        details: t('common:savingError'),
        variant: 'detailedError'
      });
      return;
    }

    enqueueSnackbar({
      message: t(`company:${snackbarLoginMethodKey}_${snackbarStateKey}_TITLE`),
      details: t(`company:${snackbarLoginMethodKey}_${snackbarStateKey}_HINT_${platform}`),
      variant: 'detailedSuccess'
    });
  };

  const getTexts = (loginMethod: string, state: string, text: string) =>
    t(`company:${loginMethod}_${state}_${text}_${platform}`, {
      returnObjects: true
    }) as string[];

  const activationContent = (loginMethod: string) => (
    <Box>
      <Typography sx={{ fontSize: '16px' }}>{t(`company:ACTIVATION_TEXT`)}</Typography>
      <Typography
        sx={{ paddingTop: 2.5, paddingBottom: 0.1, fontSize: '16px' }}
        variant="subtitle1"
      >
        {t(`company:ACTIVATION_HEADING_1`)}
      </Typography>
      <Typography component="div" sx={{ fontSize: '16px' }}>
        <ul className="list-disc pl-5 space-y-2">
          {getTexts(loginMethod, 'ACTIVATION', 'CONSIDERATIONS').map((text: string) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      </Typography>
      <Typography
        sx={{ paddingTop: 0.1, paddingBottom: 0.1, fontSize: '16px' }}
        variant="subtitle1"
      >
        {t(`company:ACTIVATION_HEADING_2`)}
      </Typography>
      <Typography component="div" sx={{ paddingBottom: 0.75, fontSize: '16px' }}>
        <ul className="list-disc pl-5 space-y-2">
          {getTexts(loginMethod, 'ACTIVATION', 'PRECONDITIONS').map((text: string) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      </Typography>
      <FeedbackAlert
        title={t('common:HINTS')}
        description={t(`company:ACTIVATION_HINTS_${platform}`, { returnObjects: true }) as string[]}
        severity="info"
        slim={true}
      />
    </Box>
  );

  const deactivationContent = (loginMethod: string) => (
    <Box>
      <Typography sx={{ fontSize: '16px' }}>
        {t(`company:DEACTIVATION_TEXT_${platform}`)}
      </Typography>
      <Typography
        sx={{ paddingTop: 2.5, paddingBottom: 0.1, fontSize: '16px' }}
        variant="subtitle1"
      >
        {t(`company:DEACTIVATION_HEADING_1`)}
      </Typography>
      <Typography component="div" sx={{ paddingBottom: 0.75, fontSize: '16px' }}>
        <ul className="list-disc pl-5 space-y-2">
          {getTexts(loginMethod, 'DEACTIVATION', 'CONSIDERATIONS').map((text: string) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      </Typography>
      <FeedbackAlert
        title={t(`company:DEACTIVATION_HINTS_TITLE`)}
        description={
          t(`company:${loginMethod}_DEACTIVATION_HINT_DESCRIPTION_${platform}`, {
            returnObjects: true
          }) as string[]
        }
        severity="warning"
        slim={true}
      />
    </Box>
  );

  return (
    <div className="flex flex-col items-start gap-10 shrink-0">
      <table className="w-full">
        <tbody>
          <tr>
            <td className="py-4 pr-4">
              <div className="flex flex-col max-w-[600px] gap-1.5">
                <Typography className="font-medium text-[16px] leading-[24px]">
                  {t('company:PASSWORD_LOGIN')}
                </Typography>
                <Typography className="text-[14px] leading-[20px]">
                  {t(`company:PASSWORD_LOGIN_HINT_${platform}`)}
                </Typography>
              </div>
            </td>
            <td className="w-24" />
            <td className="w-40">
              <div className="flex justify-end items-center space-x-2">
                {data?.password_auth_enabled ? (
                  <>
                    <StatusBadge label={t('common:ACTIVE_CAPS')} hideIcon={true} />
                    <Button
                      variant="tertiary"
                      className="text-[#D72638]"
                      onClick={async () => {
                        data.single_sign_on_enabled
                          ? setOpenPasswordDeactivationDialog(true)
                          : enqueueSnackbar({
                              message: t('company:DEACTIVATION_HINT_TITLE'),
                              details: t('company:PASSWORD_DEACTIVATION_HINT'),
                              variant: 'detailedError'
                            });
                      }}
                    >
                      {t('common:DEACTIVATE')}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={async () => {
                      setOpenPasswordActivationDialog(true);
                    }}
                  >
                    {t('common:ACTIVATE')}
                  </Button>
                )}
              </div>
            </td>
          </tr>

          <tr>
            <td colSpan={3} className="py-2">
              <div className="w-full h-px bg-grey-50" />
            </td>
          </tr>

          <tr>
            <td className="py-4 pr-4">
              <div className="flex flex-col max-w-[600px] gap-1.5">
                <Typography className="font-medium text-[16px] leading-[24px]">
                  {t(`company:SSO_LOGIN_${platform}`)}
                </Typography>
                <Typography className="text-[14px] leading-[20px]">
                  {t(`company:SSO_LOGIN_HINT_${platform}`)}
                </Typography>
              </div>
            </td>
            <td className="w-24" />
            <td className="w-40">
              <div className="flex justify-end items-center space-x-2">
                {data?.single_sign_on_enabled ? (
                  <>
                    <StatusBadge label={t('common:ACTIVE_CAPS')} hideIcon={true} />
                    <Button
                      variant="tertiary"
                      className="text-[#D72638]"
                      onClick={async () => {
                        data.password_auth_enabled
                          ? setOpenSsoDeactivationDialog(true)
                          : enqueueSnackbar({
                              message: t('company:DEACTIVATION_HINT_TITLE'),
                              details: t('company:SSO_DEACTIVATION_HINT'),
                              variant: 'detailedError'
                            });
                      }}
                    >
                      {t('common:DEACTIVATE')}
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={async () => {
                      setOpenSsoActivationDialog(true);
                    }}
                  >
                    {t('common:ACTIVATE')}
                  </Button>
                )}{' '}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div className="w-full text-sm">
        <FeedbackAlert
          title={t('common:HINTS')}
          description={t(`company:SSO_HINTS_${platform}`, { returnObjects: true }) as string[]}
          severity="info"
        />
      </div>

      <InformationDialog
        open={openSsoActivationDialog}
        content={activationContent('SSO')}
        title={t('company:SSO_ACTIVATION_TITLE')}
        confirmButtonText={t('common:ACTIVATE')}
        cancelButtonText={t('common:NOT_NOW')}
        onConfirm={async () => {
          await handleConfirmation(true, true);
        }}
        onClose={() => {
          setOpenSsoActivationDialog(false);
        }}
        onCancel={() => {
          setOpenSsoActivationDialog(false);
        }}
      />
      <InformationDialog
        open={openSsoDeactivationDialog}
        content={deactivationContent('SSO')}
        title={t('company:SSO_DEACTIVATION_TITLE')}
        confirmButtonText={t('common:DEACTIVATE')}
        confirmButtonColor="error"
        cancelButtonText={t('common:NOT_NOW')}
        onConfirm={async () => {
          await handleConfirmation(true, false);
        }}
        onClose={() => {
          setOpenSsoDeactivationDialog(false);
        }}
        onCancel={() => {
          setOpenSsoDeactivationDialog(false);
        }}
      />
      <InformationDialog
        open={openPasswordActivationDialog}
        content={activationContent('PASSWORD')}
        title={t('company:PASSWORD_ACTIVATION_TITLE')}
        confirmButtonText={t('common:ACTIVATE')}
        cancelButtonText={t('common:NOT_NOW')}
        onConfirm={async () => {
          await handleConfirmation(false, true);
        }}
        onClose={() => {
          setOpenPasswordActivationDialog(false);
        }}
        onCancel={() => {
          setOpenPasswordActivationDialog(false);
        }}
      />
      <InformationDialog
        open={openPasswordDeactivationDialog}
        content={deactivationContent('PASSWORD')}
        title={t('company:PASSWORD_DEACTIVATION_TITLE')}
        confirmButtonText={t('common:DEACTIVATE')}
        confirmButtonColor="error"
        cancelButtonText={t('common:NOT_NOW')}
        onConfirm={async () => {
          await handleConfirmation(false, false);
        }}
        onClose={() => {
          setOpenPasswordDeactivationDialog(false);
        }}
        onCancel={() => {
          setOpenPasswordDeactivationDialog(false);
        }}
      />
    </div>
  );
};
